import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { FilterType } from "./filters.types";

import { ZERO } from "@/app/constants/constants";

interface UseFilterMeasurementsReturnType {
  containerRef: RefObject<HTMLDivElement>;
  width: number;
  measuredWidths: Record<string, number>;
}

/**
 * Measures filter elements and container width for responsive overflow handling.
 * Uses ResizeObserver with RAF debouncing for performant measurement tracking.
 *
 * @param filters {FilterType<T, P>} - Object containing filter key-value pairs
 * @returns {UseFilterMeasurementsReturnType} - Measurements
 *
 */
export const useFilterMeasurements = <T, P>(
  filters: FilterType<T, P>,
): UseFilterMeasurementsReturnType => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [measuredWidths, setMeasuredWidths] = useState<Record<string, number>>(
    {},
  );

  const lastWidthsRef = useRef<Record<string, number>>({});
  const filterKeysRef = useRef<string[]>([]);
  const rafRef = useRef<number>();

  const measureSync = useCallback((): void => {
    const node = containerRef.current;

    if (!node) {
      return;
    }

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    const containerWidth = node.getBoundingClientRect().width;

    setWidth(containerWidth);

    const elements = node.querySelectorAll("[data-filter-key]");
    const nextWidths: Record<string, number> = {};
    let hasChanged = false;

    elements.forEach((el) => {
      const key = el.getAttribute("data-filter-key");
      const w = el.getBoundingClientRect().width;

      if (key && w > ZERO) {
        nextWidths[key] = w;

        if (lastWidthsRef.current[key] !== w) {
          hasChanged = true;
        }
      }
    });

    if (!hasChanged) {
      const prevKeys = Object.keys(lastWidthsRef.current);
      const nextKeys = Object.keys(nextWidths);

      if (prevKeys.length !== nextKeys.length) {
        hasChanged = true;
      }
    }

    if (hasChanged) {
      lastWidthsRef.current = nextWidths;
      setMeasuredWidths(nextWidths);
    }
  }, []);

  const measureDebounced = useCallback((): void => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(measureSync);
  }, [measureSync]);

  useLayoutEffect(() => {
    const node = containerRef.current;

    if (!node) {
      return undefined;
    }

    // initial measurement
    measureSync();

    const observer = new ResizeObserver(measureDebounced);

    observer.observe(node);

    return (): void => {
      observer.disconnect();

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [measureDebounced, measureSync]);

  useLayoutEffect(() => {
    const currentKeys = Object.keys(filters);
    const prevKeys = filterKeysRef.current;
    const hasChanged =
      currentKeys.length !== prevKeys.length ||
      !currentKeys.every((key) => prevKeys.includes(key));

    if (hasChanged) {
      filterKeysRef.current = currentKeys;
      measureSync();
    }
  }, [filters, measureSync]);

  return {
    containerRef,
    width,
    measuredWidths,
  };
};
